import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTableOne,
  fetchTableTwo,
  fetchTableThree,
  toggleLiverun,
  setSelectedDate,
} from "../reducers/activityFrameSlice";

// Colors for charts
const COLORS = {
  filed: "#4caf50",
  filed_abnormal: "#ff9800",
  not_filed_range: "#f44336",
  not_filed_setup: "#2196f3",
  not_filed_already: "#9c27b0",
};

const TABLE_THREE_COLUMNS = [
  { field: "emis_id", headerName: "EMIS ID", width: 120 },
  { field: "category", headerName: "Category", width: 280 },
  { field: "status", headerName: "Status", width: 80 },
  { field: "endtime", headerName: "End Time", width: 120 },
  { field: "notes", headerName: "Notes", width: 600 },
];

const ActivityFrame = () => {
  const dispatch = useDispatch();

  const {
    liverun,
    tableOne,
    tableTwo,
    tableThree,
    selectedDate,
    loadingOne,
    loadingTwo,
    loadingThree,
  } = useSelector((state) => state.activityFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchTableOne({ liverun, license_key }));
    }
  }, [liverun, license_key, dispatch]);

  const handleChartClick = (data) => {
    const dateTitle = data?.activeLabel;
    if (dateTitle) {
      dispatch(setSelectedDate(dateTitle));
      dispatch(fetchTableTwo({ date: dateTitle, liverun, license_key }));
      dispatch(fetchTableThree({ date: dateTitle, liverun, license_key }));
    }
  };

  return (
    <Box sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
      <Paper elevation={2} sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Activity - {liverun ? "Live Data" : "Dry Run Data"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
              />
            }
            label="Live / Dry Run"
          />
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Date-wise Filings
        </Typography>
        {loadingOne ? (
          <CircularProgress />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={tableOne}
              margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
              onClick={handleChartClick}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="filed"
                stroke={COLORS.filed}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="filed_abnormal"
                stroke={COLORS.filed_abnormal}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="not_filed_range"
                stroke={COLORS.not_filed_range}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="not_filed_setup"
                stroke={COLORS.not_filed_setup}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="not_filed_already"
                stroke={COLORS.not_filed_already}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {selectedDate && (
        <>
          <Paper elevation={2} sx={{ mt: 2, pt: 2, pb: 2, px: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Category Data for {selectedDate}
            </Typography>
            {loadingTwo ? (
              <CircularProgress />
            ) : (
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Box
                  sx={{
                    width: Math.max(tableTwo.length * 120, 800),
                    height: 400,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={tableTwo}
                      margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="category"
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="filed" fill={COLORS.filed} />
                      <Bar
                        dataKey="filed_abnormal"
                        fill={COLORS.filed_abnormal}
                      />
                      <Bar
                        dataKey="not_filed_range"
                        fill={COLORS.not_filed_range}
                      />
                      <Bar
                        dataKey="not_filed_setup"
                        fill={COLORS.not_filed_setup}
                      />
                      <Bar
                        dataKey="not_filed_already"
                        fill={COLORS.not_filed_already}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </Paper>

          <Paper elevation={2} sx={{ mt: 2, pt: 2, pb: 2, px: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Day Data for {selectedDate}
            </Typography>
            {loadingThree ? (
              <CircularProgress />
            ) : (
              <DataGrid
                rowHeight={25}
                rows={tableThree}
                columns={TABLE_THREE_COLUMNS}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ActivityFrame;
